import React, { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import BulkProductMarkupUpload from './BulkProductMarkupUpload'
import {
  removeProductMarkup,
  saveProductMarkup,
  searchProductsForMarkup,
} from '../../services/customer'

const formatCategoryPricing = (pricing) => {
  if (!pricing) return 'Not configured'
  return pricing.adjustment_type === 'percentage'
    ? `${Number(pricing.adjustment_value)}%`
    : `$${Number(pricing.adjustment_value).toFixed(3)} per item`
}

const formatOverride = (override) => (
  override.adjustment_type === 'fixed_amount'
    ? `$${Number(override.adjustment_value).toFixed(3)} per item`
    : `${Number(override.adjustment_value ?? override.markup_percentage)}%`
)

const ProductMarkupOverrides = ({ customerId, initialOverrides = [], onChanged, token }) => {
  const [overrides, setOverrides] = useState(initialOverrides)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [adjustmentType, setAdjustmentType] = useState('percentage')
  const [adjustmentValue, setAdjustmentValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => setOverrides(initialOverrides), [initialOverrides])

  useEffect(() => {
    if (!dialogOpen || editing || query.trim().length < 2) {
      setOptions([])
      return undefined
    }
    const timer = window.setTimeout(() => {
      setSearching(true)
      searchProductsForMarkup(token, customerId, query).then((response) => {
        setSearching(false)
        if (response?.status) {
          setError(response.data?.message || 'Products could not be searched.')
          return
        }
        setOptions(response?.products ?? [])
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [customerId, dialogOpen, editing, query, token])

  const closeDialog = () => {
    setDialogOpen(false)
    setEditing(null)
    setSelectedProduct(null)
    setAdjustmentType('percentage')
    setAdjustmentValue('')
    setQuery('')
    setError('')
  }

  const openEdit = (override) => {
    setEditing(override)
    setSelectedProduct(override.product)
    setAdjustmentType(override.adjustment_type || 'percentage')
    setAdjustmentValue(String(Number(override.adjustment_value ?? override.markup_percentage)))
    setError('')
    setDialogOpen(true)
  }

  const save = () => {
    const product = editing?.product ?? selectedProduct
    if (!product || adjustmentValue === '' || Number(adjustmentValue) < 0) return
    setSaving(true)
    setError('')
    saveProductMarkup(token, customerId, product.id, adjustmentType, adjustmentValue).then((response) => {
      setSaving(false)
      if (response?.status) {
        setError(Object.values(response.data?.errors ?? {}).flat().join(' ') || response.data?.message || 'Override could not be saved.')
        return
      }
      const saved = response.override
      setOverrides((current) => [saved, ...current.filter((item) => item.product_id !== saved.product_id)])
      onChanged?.()
      closeDialog()
    })
  }

  const remove = (override) => {
    if (!window.confirm(`Remove the override for ${override.product?.name || override.product?.['item-number']}?`)) return
    removeProductMarkup(token, customerId, override.product_id).then((response) => {
      if (response?.status) {
        setError(response.data?.message || 'Override could not be removed.')
        return
      }
      setOverrides((current) => current.map((item) => item.product_id === override.product_id ? { ...item, active: false } : item))
      onChanged?.()
    })
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Stack alignItems={{ xs: 'stretch', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography component="h2" fontWeight={650} variant="h6">Product markup overrides</Typography>
          <Typography color="text.secondary" variant="body2">Set a customer-specific percentage or fixed dollar markup for selected products. It takes priority over the category rate.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <BulkProductMarkupUpload customerId={customerId} token={token} onChanged={onChanged} />
          <Button onClick={() => setDialogOpen(true)} startIcon={<AddOutlinedIcon />} sx={{ minHeight: 44, whiteSpace: 'nowrap' }} variant="contained">Add override</Button>
        </Stack>
      </Stack>

      {error && <Alert onClose={() => setError('')} severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table aria-label="Product markup overrides" size="small" sx={{ minWidth: 860 }}>
          <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Category rate</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Product override</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {overrides.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>No product overrides configured.</TableCell></TableRow>}
            {overrides.map((override) => (
              <TableRow key={override.product_id} sx={{ opacity: override.active ? 1 : 0.62 }}>
                <TableCell><Stack alignItems="center" direction="row" spacing={1.5}>
                  <Avatar src={override.product?.image || undefined} variant="rounded"><ImageNotSupportedOutlinedIcon fontSize="small" /></Avatar>
                  <Box><Typography fontWeight={600} variant="body2">{override.product?.name || 'Unknown product'}</Typography><Typography color="text.secondary" variant="caption">{override.product?.['item-number'] || `#${override.product_id}`}</Typography></Box>
                </Stack></TableCell>
                <TableCell>{override.product?.category || 'N/A'}</TableCell>
                <TableCell>{formatCategoryPricing(override.category_pricing)}</TableCell>
                <TableCell><Typography fontWeight={700}>{formatOverride(override)}</Typography></TableCell>
                <TableCell><Chip color={override.active ? 'success' : 'default'} label={override.active ? 'Active' : 'Removed'} size="small" variant="outlined" /></TableCell>
                <TableCell align="right">
                  <Tooltip title={override.active ? 'Edit override' : 'Reactivate override'}><IconButton aria-label={`Edit ${override.product?.name || 'product'} override`} onClick={() => openEdit(override)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  {override.active && <Tooltip title="Remove override"><IconButton aria-label={`Remove ${override.product?.name || 'product'} override`} color="error" onClick={() => remove(override)}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog fullWidth maxWidth="sm" onClose={closeDialog} open={dialogOpen}>
        <DialogTitle>{editing ? 'Edit product override' : 'Add product override'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {editing ? <Box><Typography fontWeight={650}>{editing.product?.name}</Typography><Typography color="text.secondary" variant="body2">{editing.product?.['item-number']} · {editing.product?.category}</Typography></Box> : (
              <Autocomplete
                filterOptions={(items) => items}
                getOptionDisabled={(option) => !option.eligible}
                getOptionLabel={(option) => `${option.name || option['item-number']} · ${option['item-number'] || option.upc || ''}`}
                loading={searching}
                onChange={(_, value) => { setSelectedProduct(value); setError('') }}
                onInputChange={(_, value) => setQuery(value)}
                options={options}
                renderInput={(params) => <TextField {...params} helperText="Search by product name, item number, or UPC." InputProps={{ ...params.InputProps, endAdornment: <>{searching ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> }} label="Product" />}
                renderOption={(props, option) => <Box component="li" {...props} key={option.id}><Box><Typography variant="body2">{option.name || option['item-number']}</Typography><Typography color="text.secondary" variant="caption">{option['item-number']} · {option.category || 'N/A'}{!option.eligible ? ' · Category pricing required' : ''}</Typography></Box></Box>}
                value={selectedProduct}
              />
            )}
            {(editing || selectedProduct) && <Alert severity={(editing?.category_pricing || selectedProduct?.category_pricing) ? 'info' : 'warning'}>Category rate: {formatCategoryPricing(editing?.category_pricing || selectedProduct?.category_pricing)}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Adjustment type"
                onChange={(event) => setAdjustmentType(event.target.value)}
                select
                value={adjustmentType}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed_amount">Fixed cost</MenuItem>
              </TextField>
              <TextField
                error={adjustmentValue !== '' && Number(adjustmentValue) < 0}
                fullWidth
                helperText={adjustmentType === 'percentage' ? 'Added as a percentage of the base price.' : 'Added to each item as a fixed dollar amount.'}
                inputProps={{ min: 0, step: '0.001' }}
                InputProps={{
                  startAdornment: adjustmentType === 'fixed_amount' ? <InputAdornment position="start">$</InputAdornment> : null,
                  endAdornment: adjustmentType === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null,
                }}
                label="Adjustment value"
                onChange={(event) => setAdjustmentValue(event.target.value)}
                type="number"
                value={adjustmentValue}
              />
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button disabled={saving} onClick={closeDialog}>Cancel</Button><Button disabled={saving || (!editing && !selectedProduct?.eligible) || adjustmentValue === '' || Number(adjustmentValue) < 0} onClick={save} variant="contained">{saving ? 'Saving…' : editing?.active === false ? 'Reactivate' : 'Save override'}</Button></DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductMarkupOverrides
